import React from 'react';
import { Box, ButtonBase } from '@mui/material';
import { plr } from './tokens';

export type PlrOption<V extends string> = {
  value: V,
  title: string,
  description?: string,
};

type PlrOptionsProps<V extends string> = {
  options: PlrOption<V>[],
  value: V | '',
  onChange: (value: V) => void,
  // Nombre accesible del grupo (aria-label) o id del texto que lo nombra (aria-labelledby).
  label?: string,
  labelledBy?: string,
  // "cards": una tarjeta por opción con descripción; "pills": opciones cortas en una fila.
  layout?: 'cards' | 'pills',
  testIdPrefix?: string,
  disabled?: boolean,
  error?: boolean,
  // Tarjetas más bajas (respuestas cortas, p. ej. las preguntas de Experian).
  dense?: boolean,
};

// Grupo de opciones del rediseño PLR (reemplaza a los Select con "Seleccionar...").
// Se comporta como un radiogroup: flechas para moverse entre opciones, Espacio/Enter para elegir.
export default function PlrOptions<V extends string>(props: PlrOptionsProps<V>) {
  const {
    options, value, onChange, label, labelledBy, layout = 'cards', testIdPrefix, disabled = false,
    error = false, dense = false,
  } = props;
  const refs = React.useRef<(HTMLButtonElement | null)[]>([]);
  const selectedIndex = options.findIndex((option) => option.value === value);
  const focusIndex = selectedIndex >= 0 ? selectedIndex : 0;
  const pills = layout === 'pills';
  const borderColor = (selected: boolean) => {
    if (selected) return plr.ink;
    return error ? '#C0392B' : plr.border;
  };
  let cardPadding: string | { xs: string, sm: string } = { xs: '13px 14px', sm: '14px 16px' };
  if (pills) cardPadding = '0 6px';
  else if (dense) cardPadding = '11px 14px';

  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    const forward = event.key === 'ArrowDown' || event.key === 'ArrowRight';
    const backward = event.key === 'ArrowUp' || event.key === 'ArrowLeft';
    if (!forward && !backward) return;
    event.preventDefault();
    const next = (index + (forward ? 1 : -1) + options.length) % options.length;
    refs.current[next]?.focus();
    onChange(options[next].value);
  };

  return (
    <Box
      role="radiogroup"
      aria-label={label}
      aria-labelledby={labelledBy}
      sx={{
        display: pills ? 'grid' : 'flex',
        flexDirection: 'column',
        gridTemplateColumns: pills ? `repeat(${options.length}, minmax(0, 1fr))` : undefined,
        gap: pills ? '8px' : '10px',
      }}
    >
      {options.map((option, index) => {
        const selected = option.value === value;
        return (
          <ButtonBase
            key={option.value}
            ref={(node: HTMLButtonElement | null) => { refs.current[index] = node; }}
            role="radio"
            aria-checked={selected}
            tabIndex={index === focusIndex ? 0 : -1}
            disabled={disabled}
            data-pw={testIdPrefix ? `${testIdPrefix}-${option.value}` : undefined}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            sx={{
              display: 'flex',
              alignItems: pills ? 'center' : 'flex-start',
              justifyContent: pills ? 'center' : 'flex-start',
              gap: '12px',
              width: '100%',
              minHeight: pills ? { xs: 50, sm: 52 } : undefined,
              boxSizing: 'border-box',
              p: cardPadding,
              borderRadius: '16px',
              textAlign: pills ? 'center' : 'left',
              fontFamily: plr.font,
              background: selected ? '#F7F5FD' : '#FFFFFF',
              // Borde de 2 px como sombra: la tarjeta no cambia de tamaño al elegirla.
              boxShadow: `inset 0 0 0 ${selected ? 2 : 1}px ${borderColor(selected)}`,
              transition: 'box-shadow .15s, background-color .15s',
              '&:hover': { boxShadow: `inset 0 0 0 ${selected ? 2 : 1}px ${selected ? plr.ink : plr.faint}` },
              '&.Mui-focusVisible': { outline: `3px solid ${plr.stepCurrent}`, outlineOffset: '2px' },
              '&.Mui-disabled': { opacity: 0.6 },
            }}
          >
            {!pills && (
              <Box
                component="span"
                aria-hidden="true"
                sx={{
                  mt: '1px',
                  flexShrink: 0,
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  boxSizing: 'border-box',
                  border: `2px solid ${selected ? plr.ink : plr.faint}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&::after': selected ? {
                    content: '""', width: 10, height: 10, borderRadius: '50%', background: plr.ink,
                  } : undefined,
                }}
              />
            )}
            <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <Box
                component="span"
                sx={{
                  fontSize: { xs: 15, sm: 16 },
                  fontWeight: selected || (!pills && !dense) ? 700 : 600,
                  color: plr.ink,
                  lineHeight: 1.3,
                }}
              >
                {option.title}
              </Box>
              {option.description && (
                <Box
                  component="span"
                  sx={{
                    fontSize: { xs: 13, sm: 14 }, lineHeight: 1.4, color: plr.muted,
                  }}
                >
                  {option.description}
                </Box>
              )}
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  );
}

PlrOptions.defaultProps = {
  label: undefined,
  labelledBy: undefined,
  layout: 'cards',
  testIdPrefix: undefined,
  disabled: false,
  error: false,
  dense: false,
};
